import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  LayoutDashboard,
  FileText,
  PenTool,
  Gift,
  BookOpen,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Newspaper,
  Users,
  Shield,
  Wallet,
  Crown,
} from "lucide-react";

const DashboardLayout = ({ isAdmin = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const isPro = user?.is_pro || (user?.total_deposited >= 1000);
  const balance = user?.balance || 0;

  const customerNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/manage-pr", label: "Manage PR", icon: FileText },
    { path: "/add-funds", label: "Add Funds", icon: Wallet },
    { path: "/ai-writer", label: "AI Writer Generator", icon: PenTool },
    { path: "/partner-privilege", label: "Partner Privilege", icon: Gift },
    { path: "/resources", label: "Resources", icon: BookOpen },
    { path: "/settings", label: "Settings", icon: Settings },
  ];

  const adminNavItems = [
    { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/users", label: "Users", icon: Users },
    { path: "/admin/press-releases", label: "Press Releases", icon: FileText },
  ];

  const navItems = isAdmin ? adminNavItems : customerNavItems;

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-slate-900 transition-all duration-300 ${
          sidebarCollapsed ? "w-20" : "w-64"
        } ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
          <div className="flex items-center">
            <div className="bg-blue-600 p-2 rounded-lg">
              <Newspaper className="h-5 w-5 text-white" />
            </div>
            {!sidebarCollapsed && (
              <span className="ml-3 text-lg font-heading font-bold text-white">
                {isAdmin ? "Admin" : "PR Link Boost"}
              </span>
            )}
          </div>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-800 text-slate-400"
            data-testid="sidebar-collapse-btn"
          >
            <ChevronLeft className={`h-4 w-4 transition-transform ${sidebarCollapsed ? "rotate-180" : ""}`} />
          </button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-800 text-slate-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Workspace selector */}
        {!sidebarCollapsed && (
          <div className="px-4 py-4">
            <div className="bg-slate-800 rounded-lg px-3 py-2">
              <p className="text-xs text-slate-400 mb-1">Workspace</p>
              <p className="text-sm text-white font-medium truncate">
                {user?.company || user?.name || "My Workspace"}
              </p>
            </div>
          </div>
        )}

        {/* Submit PR Button */}
        {!isAdmin && (
          <div className="px-4 py-2">
            <Button
              data-testid="sidebar-submit-pr-btn"
              onClick={() => {
                navigate("/submit-pr");
                setSidebarOpen(false);
              }}
              className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-medium ${
                sidebarCollapsed ? "px-2" : ""
              }`}
            >
              {sidebarCollapsed ? "+" : "+ Submit PR"}
            </Button>
          </div>
        )}

        {/* Navigation */}
        <nav className="px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/dashboard" || item.path === "/admin"}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? "bg-slate-800 text-white"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {!sidebarCollapsed && <span className="ml-3 text-sm font-medium">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Admin/Customer toggle */}
        {user?.role === "admin" && (
          <div className="absolute bottom-20 left-0 right-0 px-3">
            <NavLink
              to={isAdmin ? "/dashboard" : "/admin"}
              className="flex items-center px-3 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
              data-testid="admin-toggle-btn"
            >
              <Shield className="h-5 w-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="ml-3 text-sm font-medium">
                  {isAdmin ? "Customer View" : "Admin Panel"}
                </span>
              )}
            </NavLink>
          </div>
        )}

        {/* Logout */}
        <div className="absolute bottom-4 left-0 right-0 px-3">
          <button
            onClick={logout}
            className="flex items-center w-full px-3 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="ml-3 text-sm font-medium">Log Out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? "lg:ml-20" : "lg:ml-64"} flex flex-col min-h-screen`}>
        {/* Pro promo bar */}
        {!isAdmin && !isPro && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2">
            <div className="flex items-center justify-center text-sm">
              <Crown className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                <strong>Unlock PRO rates!</strong> Deposit $1,000+ total to save up to 50% on all distribution channels.{" "}
                <button onClick={() => navigate("/add-funds")} className="underline font-semibold">
                  Add Funds →
                </button>
              </span>
            </div>
          </div>
        )}

        {/* Balance bar */}
        {!isAdmin && (
          <div className="bg-slate-100 border-b border-slate-200 px-4 py-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <span className="text-slate-600">
                  Balance: <strong className="text-slate-900">${balance.toFixed(2)}</strong>
                </span>
                {isPro && (
                  <span className="flex items-center gap-1 text-amber-600">
                    <Crown className="h-4 w-4" />
                    <strong>PRO</strong>
                  </span>
                )}
              </div>
              <button 
                onClick={() => navigate("/add-funds")}
                className="text-blue-600 hover:underline font-medium"
              >
                Add Funds
              </button>
            </div>
          </div>
        )}

        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-14 px-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-slate-100"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5 text-slate-600" />
            </button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8 border-2 border-blue-100">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-blue-600 text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <p className="text-xs text-slate-400 mt-1">Balance: ${balance.toFixed(2)}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/add-funds")}>
                  <Wallet className="h-4 w-4 mr-2" />
                  Add Funds
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate(isAdmin ? "/dashboard" : "/admin")}>
                    <Shield className="h-4 w-4 mr-2" />
                    {isAdmin ? "Customer View" : "Admin Panel"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-blue-600" data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>

        {/* Footer promo banner */}
        {!isAdmin && (
          <footer className="bg-slate-900 text-white px-4 py-3">
            <div className="flex items-center justify-center text-sm">
              <Crown className="h-4 w-4 mr-2 text-amber-400" />
              <span>
                <strong className="text-amber-400">PRO Tip:</strong> Deposit $1,000 or more to unlock exclusive discounted rates on all distribution channels!
              </span>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default DashboardLayout;
